import { useState, useEffect } from 'react';
import { KPICard, DataTable } from '../../components/shared/KPIComponents';
import { api, getCurrentMonth, formatCurrency } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { RefreshCw, TrendingUp, Users, Calendar, ChevronLeft, ChevronRight, Activity } from 'lucide-react';
import { toast } from 'sonner';
import OptimizeButton from '../../components/shared/OptimizeButton';

const MONTHS_FR = [
  'JANVIER', 'FEVRIER', 'MARS', 'AVRIL', 'MAI', 'JUIN',
  'JUILLET', 'AOUT', 'SEPTEMBRE', 'OCTOBRE', 'NOVEMBRE', 'DECEMBRE'
];

const PatronCALive = () => {
  const [caData, setCaData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [totals, setTotals] = useState({ ca: 0, couverts: 0, ticketMoyen: 0 });
  const [comparison, setComparison] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const currentMonth = getCurrentMonth();
  const sheetMonth = `${MONTHS_FR[selectedMonth]} ${selectedYear}`;

  const prevMonth = () => {
    if (selectedMonth === 0) { setSelectedMonth(11); setSelectedYear(selectedYear - 1); }
    else { setSelectedMonth(selectedMonth - 1); }
  };

  const nextMonth = () => {
    if (selectedMonth === 11) { setSelectedMonth(0); setSelectedYear(selectedYear + 1); }
    else { setSelectedMonth(selectedMonth + 1); }
  };

  const fetchCAData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/ca/${sheetMonth.replace(' ', '-')}`);
      const data = response.data;
      setCaData(data.days || []);
      setTotals({ ca: data.total_ca_ht || 0, caTTC: data.total_ca_ttc || 0, tva: data.total_tva || 0, couverts: data.total_couverts || 0, ticketMoyen: data.ticket_moyen || 0 });
      localStorage.setItem(`lemas_ca_${currentMonth}`, JSON.stringify({
        jour: data.days?.slice(-1)[0]?.ca_ht || 0, mois: data.total_ca_ht || data.total_ca_ttc || 0, date: new Date().toISOString()
      }));
      setLastSync(new Date());
      toast.success('Donnees CA synchronisees');
    } catch (error) {
      console.error('Error fetching CA:', error);
      const stored = localStorage.getItem(`lemas_ca_${currentMonth}`);
      if (stored) { const data = JSON.parse(stored); setTotals({ ca: data.mois, couverts: 0, ticketMoyen: 0 }); }
      toast.error('Impossible de recuperer les donnees Google Sheets');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchCAData(); }, [sheetMonth]);

  const getBestDay = () => {
    if (!caData.length) return null;
    const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const byDayOfWeek = caData.reduce((acc, day) => {
      const date = new Date(selectedYear, selectedMonth, day.jour);
      const dow = date.getDay();
      if (!acc[dow]) acc[dow] = { total: 0, count: 0 };
      acc[dow].total += day.ca_ht || day.ca_ttc;
      acc[dow].count += 1;
      return acc;
    }, {});
    let best = { day: 0, avg: 0 };
    Object.entries(byDayOfWeek).forEach(([dow, data]) => {
      const avg = data.total / data.count;
      if (avg > best.avg) best = { day: parseInt(dow), avg };
    });
    return { name: dayNames[best.day], avg: best.avg };
  };

  const bestDay = getBestDay();
  const caHT = totals.ca;
  const tvaCollectee = totals.tva || 0;

  const columns = [
    { header: 'Jour', render: (row) => <span className="font-mono">{row.jour}</span> },
    { header: 'CA HT', render: (row) => <span className="font-mono">{formatCurrency(row.ca_ht || row.ca_ttc)}</span> },
    { header: 'Couverts', render: (row) => <span className="font-mono">{row.couverts || '-'}</span> },
    { header: 'Ticket moyen', render: (row) => <span className="font-mono">{row.ticket_moyen ? formatCurrency(row.ticket_moyen) : '-'}</span> },
  ];

  return (
    <div className="space-y-6" data-testid="ca-live">
      {/* Header */}
      <div className="flex items-end justify-between animate-slide-up">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-1">Chiffre d'affaires</p>
          <h1 className="font-title text-2xl text-[rgb(var(--lm-text))]">CA Live</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={fetchCAData}
            variant="outline"
            size="sm"
            className="border-[rgb(var(--lm-gold))] text-[rgb(var(--lm-gold))] hover:bg-[rgb(var(--lm-gold)/0.1)]"
            disabled={loading}
            data-testid="refresh-ca-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Rafraichir
          </Button>
          <OptimizeButton page="ca" mois={`${selectedYear}-${String(selectedMonth).padStart(2, '0')}`} />
        </div>
      </div>

      {/* Month Selector */}
      <div className="flex items-center justify-center gap-4 py-2 animate-slide-up" style={{ animationDelay: '60ms', animationFillMode: 'backwards' }}>
        <Button variant="ghost" size="sm" onClick={prevMonth} className="text-[rgb(var(--lm-gold))] hover:bg-[rgb(var(--lm-gold)/0.1)]">
          <ChevronLeft size={20} />
        </Button>
        <div className="text-center min-w-[180px]">
          <p className="font-title text-xl" style={{ color: 'rgb(var(--lm-gold))' }}>{MONTHS_FR[selectedMonth]} {selectedYear}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={nextMonth} className="text-[rgb(var(--lm-gold))] hover:bg-[rgb(var(--lm-gold)/0.1)]">
          <ChevronRight size={20} />
        </Button>
      </div>

      {lastSync && (
        <p className="text-[10px] text-[rgb(var(--lm-text-muted))] text-center tracking-wider">
          Derniere sync: {lastSync.toLocaleTimeString('fr-FR')}
        </p>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '120ms', animationFillMode: 'backwards' }}>
          <span className="kpi-label">CA HT Mois</span>
          <p className="font-mono text-xl mt-1" style={{ color: 'rgb(var(--lm-gold))' }}>{formatCurrency(totals.ca)}</p>
        </div>
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '180ms', animationFillMode: 'backwards' }}>
          <span className="kpi-label">CA TTC</span>
          <p className="font-mono text-xl mt-1 text-[rgb(var(--lm-text))]">{formatCurrency(totals.caTTC || 0)}</p>
        </div>
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '240ms', animationFillMode: 'backwards' }}>
          <span className="kpi-label">TVA collectee</span>
          <p className="font-mono text-xl mt-1 text-[rgb(var(--lm-text))]">{formatCurrency(tvaCollectee)}</p>
        </div>
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '300ms', animationFillMode: 'backwards' }}>
          <span className="kpi-label">Ticket moyen</span>
          <p className="font-mono text-xl mt-1 text-[rgb(var(--lm-text))]">{formatCurrency(totals.ticketMoyen)}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '360ms', animationFillMode: 'backwards' }}>
          <div className="flex items-center gap-2 mb-2">
            <Users size={14} className="text-[rgb(var(--lm-text-secondary))]" />
            <span className="kpi-label">Total couverts</span>
          </div>
          <p className="kpi-value text-[rgb(var(--lm-text))]">{totals.couverts}</p>
        </div>
        {bestDay && (
          <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '420ms', animationFillMode: 'backwards' }}>
            <div className="flex items-center gap-2 mb-2">
              <Calendar size={14} style={{ color: 'rgb(var(--lm-gold))' }} />
              <span className="kpi-label">Meilleur jour</span>
            </div>
            <p className="kpi-value" style={{ color: 'rgb(var(--lm-gold))' }}>{bestDay.name}</p>
            <p className="text-[10px] text-[rgb(var(--lm-text-secondary))] font-mono mt-1">Moy: {formatCurrency(bestDay.avg)}</p>
          </div>
        )}
      </div>

      {/* Comparison N-1 */}
      {comparison && (
        <div className="lm-card p-5 animate-fade-in" style={{ borderColor: 'rgb(var(--lm-gold) / 0.2)' }}>
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={14} style={{ color: 'rgb(var(--lm-gold))' }} />
            <span className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Comparaison N-1</span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">CA N</p>
              <p className="font-mono text-[rgb(var(--lm-text))]">{formatCurrency(totals.ca)}</p>
            </div>
            <div>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">CA N-1</p>
              <p className="font-mono text-[rgb(var(--lm-text))]">{formatCurrency(comparison.ca_n1)}</p>
            </div>
            <div>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Ecart</p>
              <p className={`font-mono ${comparison.ecart >= 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>
                {comparison.ecart >= 0 ? '+' : ''}{comparison.ecart.toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Daily Table */}
      <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '480ms', animationFillMode: 'backwards' }}>
        <span className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Detail jour par jour</span>
        <div className="mt-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Activity size={24} className="animate-pulse-slow" style={{ color: 'rgb(var(--lm-gold))' }} />
              <span className="text-xs text-[rgb(var(--lm-text-muted))]">Chargement...</span>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={caData.filter(d => (d.ca_ht || d.ca_ttc) > 0)}
              emptyMessage="Aucune donnee CA pour ce mois"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PatronCALive;
